import { Input, InputFooter, InputHeader } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { token } from "@amzn/stencil-design-tokens/js/web/utils";
import styled from "@emotion/styled";
import React, { useMemo, useRef, useState } from "react";

export interface ComboBoxInputProps {
  label: string;
  testId: string;
  value: string;
  options?: string[];
  disabled?: boolean;
  required?: boolean;
  error?: string;
  isLoading?: boolean;
  loadingText?: string;
  maxVisibleOptions?: number;
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
}

const DEFAULT_MAX_VISIBLE_OPTIONS = 50;
const DEFAULT_LOADING_TEXT = "Loading...";

export const ComboBoxInput: React.FC<ComboBoxInputProps> = ({
  label,
  testId,
  value,
  options,
  disabled,
  required,
  error,
  isLoading,
  loadingText = DEFAULT_LOADING_TEXT,
  maxVisibleOptions = DEFAULT_MAX_VISIBLE_OPTIONS,
  onChange,
  onSelect,
}) => {
  const hasError = !!error;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const visibleOptions = useMemo(() => {
    const allOptions = options ?? [];
    const query = value.trim().toLowerCase();
    const matchingOptions = query
      ? allOptions.filter((option) => option.toLowerCase().includes(query))
      : allOptions;
    return matchingOptions.slice(0, maxVisibleOptions);
  }, [options, value, maxVisibleOptions]);

  const showOptions = isOpen && !disabled && !isLoading && visibleOptions.length > 0;
  const showLoading = isOpen && !disabled && !!isLoading;
  const isListVisible = showOptions || showLoading;

  const closeList = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const selectOption = (option: string) => {
    (onSelect ?? onChange)(option);
    closeList();
    inputRef.current?.blur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
    setHighlightedIndex(-1);
    setIsOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLoading && event.key !== "Escape") {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((index) => Math.min(index + 1, visibleOptions.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter":
        if (showOptions && highlightedIndex >= 0) {
          event.preventDefault();
          selectOption(visibleOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        closeList();
        break;
      default:
        break;
    }
  };

  return (
    <Col gridGap="S200">
      <Row>
        <InputHeader labelText={label} htmlFor={testId} required={required} error={hasError} />
      </Row>
      <FieldContainer>
        <Input
          ref={inputRef}
          id={testId}
          data-testid={testId}
          role="combobox"
          aria-expanded={showOptions}
          aria-invalid={hasError}
          autoComplete="off"
          width="100%"
          value={value}
          disabled={disabled}
          error={hasError}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={closeList}
          onKeyDown={handleKeyDown}
        />
        {isListVisible && (
          <OptionsList role="listbox" data-testid={`${testId}-options`}>
            {showLoading ? (
              <LoadingItem aria-disabled data-testid={`${testId}-loading`}>{loadingText}</LoadingItem>
            ) : (
              visibleOptions.map((option, index) => (
                <OptionItem
                  key={option}
                  role="option"
                  isHighlighted={index === highlightedIndex}
                  aria-selected={index === highlightedIndex}
                  data-testid={`${testId}-option-${index}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectOption(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option}
                </OptionItem>
              ))
            )}
          </OptionsList>
        )}
      </FieldContainer>
      {hasError && (
        <InputFooter id={`${testId}-error`} error data-testid={`${testId}-error`}>
          {error}
        </InputFooter>
      )}
    </Col>
  );
};

const FieldContainer = styled.div({
  position: "relative",
  width: "100%",
});

const OptionsList = styled.ul({
  position: "absolute",
  top: "calc(100% + 2px)",
  left: 0,
  right: 0,
  zIndex: 1000,
  margin: 0,
  padding: token("dimensions.spacing.200"),
  listStyle: "none",
  maxHeight: 345,
  overflowY: "auto",
  backgroundColor: token("color.surface.bg-default"),
  border: `${token("dimensions.border.width.100")} solid ${token("color.border.primary")}`,
  borderRadius: token("dimensions.border.radius.100"),
  boxShadow: `0 2px 8px ${token("color.gradient.elevation")}`,
});

const OptionItem = styled.li<{ isHighlighted: boolean }>(({ isHighlighted }) => ({
  color: token("color.text.primary"),
  padding: token("dimensions.spacing.200"),
  borderRadius: token("dimensions.border.radius.100"),
  cursor: "pointer",
  listStyle: "none",
  backgroundColor: isHighlighted
    ? token("color.action.secondary.bg-hover")
    : token("color.input.bg-default"),
  ...(isHighlighted && {
    outline: `2px solid ${token("color.border.border-focus")}`,
    outlineOffset: "1px",
  }),
  "&:hover": {
    backgroundColor: token("color.action.secondary.bg-hover"),
  },
  "&:active": {
    backgroundColor: token("color.action.secondary.bg-pressed"),
  },
}));

const LoadingItem = styled.li({
  color: token("color.text.secondary"),
  padding: token("dimensions.spacing.200"),
  listStyle: "none",
  cursor: "default",
});
